import { Component, ViewChild, TemplateRef, OnInit } from "@angular/core";
import { isSameDay, isSameMonth } from "date-fns";
import { Observable, Subject, takeUntil } from "rxjs";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarView,
} from "angular-calendar";
import { EventColor } from "calendar-utils";
import { FormBuilder, FormGroup } from "@angular/forms";
import { BaseComponent } from "src/app/base.component";
import { select, Store } from "@ngrx/store";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  loadingAdvertisingCampaignSelector,
  resultAdsByPublisherSelector,
  resultAllAdsByStatusAndPublisherSelector,
} from "src/app/store/advertisingCampaign/advertiserCampaign.selectors";
import {
  invokeAllAdsByStatusAndPublisher,
  resultAllAdsByStatusAndPublisher,
} from "src/app/store/advertisingCampaign/advertisingCampaign.actions";
import {
  CampaignStatusEnum,
  CampaignStatusEnumMapping,
} from "src/app/shared/enum/campaign-status";
import { RoleEnum } from "src/app/shared/models/user/role.interface";
import { SelectedLps } from "src/app/shared/models/location-partners/selectedLps";
import { saveSelectedLPsSelector } from "src/app/store/locationPartner/location-partner.selectors";
import { selectedAdvertiserSelector } from "src/app/store/advertiser/advertiser.selectors";
import { selectedAdvertiser } from "src/app/store/advertiser/advertiser.actions";
import { initialState } from "src/app/store/advertisingCampaign/advertisingCampaign.reducers";
import {
  calendarFilter,
  FilterInterface,
} from "src/app/shared/data/filter-data";
import { AdvertisingCampaignInterface } from "src/app/shared/models/advertising-campaigns/advertising-campaign.interface";
import { FilterCalendar } from "src/app/shared/models/advertising-campaigns/filter-calendar.interface";
import { selectedLPs } from "src/app/store/locationPartner/location-partner.action";
import { invokeApiDistroyed } from "src/app/store/apiState.interface";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import {
  catchForbiddenError,
  catchServerError,
} from "src/app/store/user/user.actions";
import {
  catchForbiddenErrorSelector,
  catchServerErrorSelector,
} from "src/app/store/user/user.selectors";
import { JwtService } from "src/app/shared/services/jwt.service";
import { Authorities } from "src/app/shared/enum/authorities";
import { FiltersService } from "src/app/shared/services/filters.service";
import { FilterParams } from "src/app/shared/models/filterParams";

const colors: Record<string, EventColor> = {
  FINISHED: {
    primary: "#7dc006",
    secondary: "#7dc006",
  },
  ONGOING: {
    primary: "#1d97ff",
    secondary: "#1d97ff",
  },
  WAITING: {
    primary: "#ff8819",
    secondary: "#ff8819",
  },
  DRAFT: {
    primary: "#3E5463",
    secondary: "#3E5463",
  },
  AWAITING_PAYMENT: {
    primary: "#ff69b4",
    secondary: "#ff69b4",
  },
  AWAITING_APPROVAL: {
    primary: "#0E4FA6",
    secondary: "#0E4FA6",
  },
};

@Component({
  selector: "app-calendar",
  templateUrl: "./calendar.component.html",
})
export class CalendarComponent extends BaseComponent implements OnInit {
  @ViewChild("modalContent", { static: true })
  modalContent!: TemplateRef<any>;

  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  viewDate: Date = new Date();

  modalData!: {
    action: string;
    event: CalendarEvent;
  };
  actions: CalendarEventAction[] = [
    {
      label: '<i class="fas fa-fw fa-pencil-alt"></i>',
      a11yLabel: "Edit",
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.handleEvent("Edited", event);
      },
    },
    {
      label: '<i class="fas fa-fw fa-trash-alt"></i>',
      a11yLabel: "Delete",
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.events = this.events.filter((iEvent) => iEvent !== event);
        this.handleEvent("Deleted", event);
      },
    },
  ];
  refresh = new Subject<void>();
  events: any[] = [];

  activeDayIsOpen: boolean = true;
  color = colors;

  /**  */
  formFilter: FormGroup;
  allAds: AdvertisingCampaignInterface[] | null = null;
  resultAllAdsByStatusAndPublisherRole$: Observable<
    AdvertisingCampaignInterface[] | null
  >;
  status: string[];
  checked: boolean = true;
  formSelectedStatus: FormGroup;
  details: AdvertisingCampaignInterface;
  selectedLps: SelectedLps = {
    companyNames: [],
    isCalculator: false,
    count: 0,
  };
  showSelectedLPs$: Observable<SelectedLps | null>;
  showSelectedAdvertisers$: Observable<string[] | null>;
  selectedAdvertisers: string[] = [];
  adByPublisher$: Observable<AdvertisingCampaignInterface[] | null>;
  adByPublisher: AdvertisingCampaignInterface[][] | null;

  filterCalendar: FilterCalendar = {
    ...initialState.filterCalendar,
  };
  options: CampaignStatusEnum[] = [];

  filter: FilterInterface = calendarFilter;
  loading$: Observable<boolean | null>;
  isFiltered: boolean = false;

  catchServerError$: Observable<boolean | null>;
  forbidden: boolean | null = false;
  loading: boolean | null;
  catchForbiddenError$: Observable<boolean | null>;

  filters: FilterParams | null;
  constructor(
    private modal: NgbModal,
    private store: Store<AppStateInterface>,
    private fb: FormBuilder,
    private saveHistory: SaveUserHistoryService,
    private jwtService: JwtService,
    private filtersService: FiltersService
  ) {
    super(store);
    this.catchServerError$ = this.store
      .pipe(select(catchServerErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.catchForbiddenError$ = this.store
      .pipe(select(catchForbiddenErrorSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.resultAllAdsByStatusAndPublisherRole$ = this.store
      .pipe(select(resultAllAdsByStatusAndPublisherSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.showSelectedLPs$ = this.store
      .pipe(select(saveSelectedLPsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.showSelectedAdvertisers$ = this.store
      .pipe(select(selectedAdvertiserSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.adByPublisher$ = this.store
      .pipe(select(resultAdsByPublisherSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loading$ = this.store
      .pipe(select(loadingAdvertisingCampaignSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.formFilter = this.fb.group({
      date: [[null, null]],
      title: [""],
    });

    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(
        Authorities.ADC_CALENDAR.toString()
      );
    }

    this.extractFilters();
  }

  extractFilters() {
    this.filters = this.filtersService.decodeUri();
    if (this.filters) {
      this.options = this.findOption(this.filters.statuses) ?? [];
    }
    this.selectedAdvertisers = this.filtersService.extractArrayValueFromUrl(
      this.filters,
      "advertisers",
      this.selectedAdvertisers
    );

    this.selectedLps.companyNames = this.filtersService.extractArrayValueFromUrl(
      this.filters,
      "locations",
      this.selectedLps.companyNames
    );

    this.filtersService.patchFormValue(
      this.filters,
      ["title", "date"],
      this.formFilter
    );

    if (this.formFilter.get("date")?.value[0]) {
      this.isFiltered = true;
    }

    this.filterCalendar = {
      ...this.filterCalendar,
      status: this.options,
      email: this.selectedAdvertisers,
      title: this.formFilter.get("title")?.value,
      startingDate: this.formFilter.get("date")?.value[0],
      endingDate: this.formFilter.get("date")?.value[1],
    };
  }
  ngOnInit(): void {
    this.catchServerError$.subscribe((result) => {
      if (result) {
        this.forbidden = true;
        this.loading = false;
        this.store.dispatch(catchServerError({ serverError: null }));
      }
    });
    this.catchForbiddenError$.subscribe((result) => {
      if (result) {
        this.store.dispatch(catchForbiddenError({ forbiddenError: null }));
      }
    });
    this.viewDate = new Date();
    this.loading$.subscribe((data) => {
      this.loading = data;
    });
    this.filter.name.map(
      (e) =>
        (e.data = Object.keys(CampaignStatusEnum)
          .filter((value) => isNaN(Number(value)) === true)
          .map(
            (key: string | any) =>
              CampaignStatusEnumMapping[key as keyof typeof CampaignStatusEnum]
          ))
    );
    this.store.dispatch(selectedLPs({ selectedLps: null }));
    this.store.dispatch(
      resultAllAdsByStatusAndPublisher({ advertisingCampaignDto: null })
    );
    this.showSelectedLPs$.subscribe((result: SelectedLps | null) => {
      if (result) {
        this.selectedLps.companyNames = result.companyNames;
        this.filterCalendar = {
          ...this.filterCalendar,
          locationsPartners : result.companyNames
        }
        this.filterData();
        if (this.selectedLps.companyNames.length == 0) {
          this.viewDate = new Date();
        }
      }
    });
    this.showSelectedAdvertisers$.subscribe((result: string[] | null) => {
      if (result) {
        this.selectedAdvertisers = result;
        this.saveOption(this.options);
      }
    });
    this.formFilter.valueChanges.subscribe((changes) => {
      this.filterCalendar = {
        ...this.filterCalendar,
        title: changes.title,
        startingDate: changes.date[0] ?? null,
        endingDate: changes.date[1] ?? null,
      };
      this.filterData();
      if (changes.date[0] != null) {
        this.viewDate = new Date(changes.date[0]);
      }
      if (changes.title != "" || changes.date[0] != null) {
        this.isFiltered = true;
      } else {
        this.isFiltered = false;
      }
    });
    this.resultAllAdsByStatusAndPublisherRole$.subscribe(
      (result: AdvertisingCampaignInterface[] | null) => {
        this.allAds = result;
        if (result) {
          this.saveHistory.saveUserHistory(
            UserHistoryActionsEnum.GET,
            UserHistoryServicesEnum.CALENDAR
          );
          this.forbidden = false;
          for (let i = 0; i < result.length; i++) {
            this.events.push({
              start: new Date(result[i].startingDate),
              end: new Date(result[i].endingDate),
              title: result[i].title,
              color: { ...colors[result[i].status] },
              advertisingCampaign: result[i],
              allDay: true,
              resizable: {
                beforeStart: true,
                afterEnd: true,
              },
              draggable: true,
            });
            this.refresh.next();
            if (this.isFiltered) {
              this.viewDate = new Date(result[i].startingDate);
            } else {
              this.viewDate = new Date();
            }
          }
        } else {
          this.filterData()
        }
      }
    );
  }

  filterData() {
    this.events = [];
    this.filterCalendar = {
      ...this.filterCalendar,
      locationsPartners: this.selectedLps.companyNames,
      roleName: RoleEnum.ROLE_ADVERTISER,
      email: this.selectedAdvertisers,
    };
    this.store.dispatch(
      invokeAllAdsByStatusAndPublisher({
        filterCalendar: this.filterCalendar,
      })
    );
  }

  dayClicked({ date, events }: { date: Date; events: any[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    }
  }
  handleEvent(action: string, event: any): void {
    this.modalData = { event, action };
    this.details = event.advertisingCampaign;
    this.modal.open(this.modalContent, { centered: true, size: "lg" });
  }
  setView(view: CalendarView) {
    this.view = view;
  }
  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  findOption(option: CampaignStatusEnum[]) {
    this.events = [];
    this.isFiltered = true;
    let listFounds: CampaignStatusEnum[] = [];
    option?.map((option) => {
      listFounds.push(
        Object.keys(CampaignStatusEnumMapping).find(
          (key) =>
            CampaignStatusEnumMapping[key as CampaignStatusEnum] === option
        ) as CampaignStatusEnum
      );
    });
    return listFounds;
  }

  saveOption(option: CampaignStatusEnum[]) {
    this.options = this.findOption(option);
    this.filterCalendar = {
      ...this.filterCalendar,
      status: this.options,
    };
    this.filterData();
  }

  removeFilter() {
    this.events = [];
    this.formFilter.reset({
      title: "",
      date: [null, null],
    });
    this.store.dispatch(
      invokeAllAdsByStatusAndPublisher({
        filterCalendar: { ...this.filterCalendar, status: [] },
      })
    );
    this.viewDate = new Date();
    this.store.dispatch(selectedAdvertiser({ selectedAdvertiser: null }));
    this.store.dispatch(selectedLPs({ selectedLps: null }));
  }
}
